import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaChevronDown } from "react-icons/fa";

let openSelectId = null;

const Select = ({
  label,
  name,
  options = [],
  value,
  onChange,
  disabled = false,
  minValue,
  maxValue,
  placeholder = "Select",
  id,
  className,
  labelClass,
}) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(
    options.find((o) => o.value === value) || null
  );
  const containerRef = useRef(null);
  const dropdownRef = useRef(null); // ✅ NEW

  const filteredOptions = options.filter((option) => {
    if (typeof minValue !== "undefined" && option.value < minValue) return false;
    if (typeof maxValue !== "undefined" && option.value > maxValue) return false;
    return true;
  });

  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  const toggleDropdown = () => {
    if (disabled) return;
    if (!open) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
    setOpen(!open);
  };

  const handleSelect = (option) => {
    setSelected(option);
    setOpen(false);
    openSelectId = null;
    if (onChange) {
      onChange({ target: { name, value: option.value } });
    }
  };

  // ✅ FIXED outside click detection
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideContainer =
        containerRef.current?.contains(event.target);
      const clickedInsideDropdown = dropdownRef.current?.contains(event.target);

      if (!clickedInsideContainer && !clickedInsideDropdown) {
        setOpen(false);
        if (openSelectId === id) openSelectId = null;
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [id]);

  useEffect(() => {
    setSelected(options.find((o) => o.value === value) || null);
  }, [options, value]);

  return (
    <div
      className={`flex flex-col relative w-full ${className}`}
      ref={containerRef}
      style={{ overflow: "visible", zIndex: open ? 999 : "auto" }}
    >
      {label && (
        <label
          htmlFor={name}
          className={`mb-2 text-sm font-medium text-gray-500 ${labelClass}`}
        >
          {label}
        </label>
      )}

      {/* Button */}
      <button
        type="button"
        disabled={disabled}
        className={`flex justify-between items-center w-full rounded-md border p-3 shadow-sm focus:ring-2 ${
          disabled
            ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-50"
            : "bg-white text-black border-gray-300 focus:border-blue-500 focus:ring-blue-500"
        }`}
        onClick={toggleDropdown}
      >
        {selected ? selected.label : (
          <span className="text-gray-400">{placeholder}</span>
        )}
        <FaChevronDown
          className={`h-4 w-4 text-gray-500 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Dropdown via portal */}
      {open &&
        !disabled &&
        createPortal(
          <ul
            ref={dropdownRef} // ✅ NEW
            className="absolute rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 max-h-60 overflow-y-auto z-[9999]"
            style={{
              position: "absolute",
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            }}
          >
            {filteredOptions.map((option) => (
              <li
                key={option.value}
                onClick={() => handleSelect(option)}
                className={`cursor-pointer px-4 py-2 hover:bg-blue-100 ${
                  selected?.value === option.value
                    ? "bg-blue-50 font-medium"
                    : ""
                }`}
              >
                {option.label}
              </li>
            ))}
          </ul>,
          document.body
        )}
    </div>
  );
};

export default Select;
